import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { ImageCroppedEvent } from 'ngx-image-cropper';
import { Subscription } from 'rxjs';
import { TranslationService } from 'src/app/core/services/translation/translation.service';

@Component({
  selector: 'app-image-upload',
  templateUrl: './image-upload.component.html',
  styleUrls: ['./image-upload.component.scss'],
})
/**
 * Image Upload Molecule
 */
export class ImageUploadComponent implements OnInit {
  @Input() fileUploadLabel!: string[];
  @Input() fileUploadAlt!: string[];
  @Input() icon = 'image';
  @Input() animateImage = false;
  @Input() abstractControl: AbstractControl | null | undefined;
  @ViewChild('fileUpload') upload!: ElementRef<HTMLInputElement>;
  imageUploadLabels = {
    saveLabel: 'cancel',
    cancelLabel: 'save-changes',
  };
  selectedFile?: File;
  fileUrl?: string | ArrayBuffer | null;
  imageError: string = '';
  showCropper: boolean = false;
  loading: boolean = false;
  imageChangedEvent: null | Event = null;
  croppedImage: any;
  private subsink = new Subscription();
  errorMessage = {
    errorReadingFile: 'error-reading-file',
    imageTypeError: 'image-type-error',
    imageSizeError: 'image-size-error',
    imageDimensionError: 'image-dimension-error',
  };

  /**
   * set up file url if avaiable from form control
   */
  ngOnInit() {
    if (this.abstractControl instanceof AbstractControl) {
      this.fileUrl = this.abstractControl.value;
      this.subsink.add(
        this.abstractControl?.valueChanges.subscribe((value) => {
          this.fileUrl = value;
        })
      );
    }
    this.translationService.translateObject(this.imageUploadLabels);
    this.translationService.translateObject(this.errorMessage);
  }

  /**
   * Constructor to import translation service
   */
  constructor(private translationService: TranslationService) {}

  /**
   * Called when a file is selected
   * @param {Event} event object used on cropper
   */
  onFileSelected(event: Event) {
    this.imageError = '';
    const target = event.target as HTMLInputElement;
    let file: File;
    if (target.files) {
      file = target.files[0];
    } else {
      // TODO: improve errors
      throw new Error(this.errorMessage.errorReadingFile);
    }
    if (file) {
      if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
        this.imageError = this.errorMessage.imageTypeError;
        return;
      }
      this.setFileUrl(file, event);
    }
  }

  /**
   * Decides which label to show in UI, dependant on an image
   * being already available or not
   * @return {string}
   */
  getFileUploadLabel(): string {
    if (this.fileUrl) {
      return this.fileUploadLabel[1];
    }
    return this.fileUploadLabel[0];
  }

  /**
   * Retrieves base64 encoded file url from a file object
   * @param {File} file get url from file
   * @param {Event} event event for imageChanged property
   */
  setFileUrl(file: File, event: Event) {
    if (this.isFileHuge(file)) {
      this.imageError = this.errorMessage.imageSizeError;
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const image = new Image();
      image.onload = () => {
        if (this.isImageValid(image)) {
          this.imageChangedEvent = event;
        } else {
          this.imageError = this.errorMessage.imageDimensionError;
        }
      };
      image.src = reader.result as string;
    };
  }

  /**
   * @param {HTMLImageElement} image
   * @return {boolean}
   */
  isImageValid(image: HTMLImageElement | ImageCroppedEvent): boolean {
    const min = 192; // px
    return image.width >= min && image.height >= min;
  }

  /**
   * @param {File} file
   * @return {boolean}
   */
  isFileHuge(file: File): boolean {
    const max = 2000000; // 2MB;
    return file.size > max;
  }

  /**
   * Cleans up resources
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * Crop event
   * @param {ImageCroppedEvent} event
   */
  imageCropped(event: ImageCroppedEvent) {
    this.croppedImage = event.base64;
  }

  /**
   * Image loaded event
   */
  imageLoaded() {
    this.showCropper = true;
  }

  /**
   * Saves cropped image
   */
  saveImage() {
    this.selectedFile = this.croppedImage;
    this.fileUrl = this.croppedImage;
    this.abstractControl?.setValue(this.fileUrl);
    this.selectedFile = this.croppedImage;
    this.showCropper = false;
    this.imageChangedEvent = null;
  }

  /**
   * Cancels crop and revert image, if exists
   */
  cancelCrop() {
    if (this.selectedFile) {
      this.croppedImage = this.selectedFile;
    }
    this.showCropper = false;
    this.removeImage();
  }

  /**
   * Cleans selected image
   */
  removeImage() {
    this.croppedImage = null;
    this.imageChangedEvent = null;
    this.fileUrl = '';
    this.abstractControl?.setValue(this.fileUrl);
    this.upload.nativeElement.value = '';
  }
}
